import React from "react";
import { motion } from "framer-motion";
import { Flame, Hammer, Soup } from "lucide-react";
import { useLang } from "../i18n/LangProvider";
import { POTENCE_STORY } from "../i18n/translations";

const Story = () => {
  const { t } = useLang();

  const pillars = [
    { icon: Flame, title: t.story.pillar1Title, desc: t.story.pillar1Desc },
    { icon: Hammer, title: t.story.pillar2Title, desc: t.story.pillar2Desc },
    { icon: Soup, title: t.story.pillar3Title, desc: t.story.pillar3Desc },
  ];

  return (
    <section
      id="story"
      data-testid="story-section"
      className="bg-obsidian py-24 md:py-32 grain"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-stretch">
          {/* Left text */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="md:col-span-5 flex flex-col justify-center"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="divider-line" />
              <span className="overline">{t.story.overline}</span>
            </div>

            <h2 className="font-serif font-light text-ivory text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-8">
              {t.story.title}
            </h2>

            <div className="space-y-5 text-ivory/70 text-base sm:text-lg leading-relaxed">
              <p className="font-serif italic text-copper-soft text-xl sm:text-2xl">
                {t.story.paragraph1}
              </p>
              <p>{t.story.paragraph2}</p>
            </div>

            {/* Pillars */}
            <div className="grid grid-cols-3 gap-4 mt-12 pt-10 border-t border-edge">
              {pillars.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div
                    key={i}
                    data-testid={`story-pillar-${i}`}
                    className="flex flex-col gap-2"
                  >
                    <Icon size={22} className="text-copper" strokeWidth={1.5} />
                    <h4 className="font-serif text-xl text-ivory">{p.title}</h4>
                    <p className="text-xs text-ivory/55 leading-snug">{p.desc}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Right image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="md:col-span-7 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={POTENCE_STORY}
                alt="Potence detail — viande, sauces et riz safrané"
                className="absolute inset-0 w-full h-full object-cover hover:scale-[1.04] transition-transform duration-[1200ms]"
              />
              {/* Frame accents */}
              <div className="absolute top-0 left-0 w-16 h-16 border-t border-l border-copper" />
              <div className="absolute bottom-0 right-0 w-16 h-16 border-b border-r border-copper" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Story;
